/**
 * ThemeContext.jsx — Global light/dark theme state for the M16S app.
 *
 * Persistence: The active theme is stored in localStorage under the key 'theme'.
 * On first load (no saved value), the theme is determined by the OS-level
 * prefers-color-scheme media query.
 *
 * DOM effect: The active theme name ('light' or 'dark') is applied as a class
 * on <html> (document.documentElement). Tailwind's dark: variants use this class
 * to switch styles.
 *
 * Provides:
 *   theme        {string}   — Current theme: 'light' or 'dark'.
 *   toggleTheme  {function} — Switches between 'light' and 'dark' and persists
 *                             the new value to localStorage.
 *
 * Usage:
 *   Wrap your tree with <ThemeProvider>. Consume via the useTheme() hook.
 */
import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  // Initialise theme from localStorage, falling back to the OS preference.
  // This runs once on mount before the first render, avoiding a flash.
  const [theme, setTheme] = useState(() => {
    // Check localStorage or system preference
    const saved = localStorage.getItem('theme');
    if (saved) return saved;

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Whenever the theme changes: remove the old class from <html>, add the new
  // one, and write the value to localStorage so it survives a page refresh.
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  /**
   * Toggles between 'light' and 'dark'. The new value is persisted to
   * localStorage via the effect above.
   */
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme — Hook to access theme context.
 * Must be called inside a component that is a descendant of <ThemeProvider>.
 * Throws if used outside the provider tree.
 * @returns {{ theme: string, toggleTheme: function }}
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
